import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Components
import DataList from '../data-list/DataList';
import EntityDetailsPopup from '../entity-details-popup/EntityDetailsPopup';
import EditIndicator from '../edit-indicator/EditIndicator';
import SubCategoryEdit from '../sub-category-edit/SubCategoryEdit';
import CategoryEdit from '../category-edit';
import PreviewSearchResult from '../preview-search-result/PreviewSearchResult';
import MessageModal from '../message-modal';

// Config
import { getDataListColDefs } from 'common/config/previewColDef.config';
import {
    DELETE_POPUP_MSG,
    DELETE_POPUP_SUCCESS,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    DELETE_POPUP_TITLE
} from 'common/config';

// Redux
import { RootState } from 'common/redux/core/root.reducer';
import {
    getFieldErrors,
    getPreviewData,
    getCurrentAction,
    getErrorCount,
    getErrorFixedStatus
} from 'common/redux/preview-data/previewData.selector';
import { previewDataActions } from 'common/redux/preview-data';
import { appStateActions } from 'common/redux/app-state';

// Models
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { Action, ApiStatus, ButtonLabel, EntityType, MessageType } from 'common/enums';
import { CategoryDTO, PreviewEntityDTO } from 'common/models/data/entity.model';
import { IndicatorUpdateDTO } from 'common/models/data/indicator.model';
import { FieldError } from 'common/models';
import { mapToFieldErrors } from 'common/utils/error';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { findCategories } from 'common/utils/table';
import { ApiInitiator } from 'common/enums/data.enum';

type PreviewDataProps = {
    searchVal?: string;
    isDataEditorPreviewDisabled: boolean;
};

const PreviewData = ({ searchVal, isDataEditorPreviewDisabled }: PreviewDataProps) => {
    const dispatch = useDispatch();

    const dataListRef = useRef<any>();
    // For handling state update issue in columnDefs
    const errorCountRef = useRef<number>(0);
    const errorFixedRef = useRef<boolean>(false);

    //selectors
    const previewData = useSelector((state: RootState) => getPreviewData(state));
    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const currentAction = useSelector((state: RootState) => getCurrentAction(state));
    const errorCount = useSelector((state: RootState) => getErrorCount(state));
    const isAtleastOneErrorFixed = useSelector((state: RootState) => getErrorFixedStatus(state));

    //states
    const [pagedRequest, setPagedRequest] = useState<PagedRequest>({
        pageNumber: 1,
        pageSize: 20,
        searchText: ''
    });
    const [editData, setEditData] = useState<EntityDetailsPayload>();
    const [deleteEntityName, setDeleteEntityName] = useState<string>('');
    /**
     * Edit click handler
     * @param data Entity data
     */
    const onEditClick = useCallback(
        (data: EntityDetailsPayload) => {
            const entity = findCategories(previewData.data?.data, data.id);
            if (entity.errors) {
                dispatch(previewDataActions.setFieldErrors(mapToFieldErrors(entity.errors)));
            }
            setEditData(data);
            dispatch(previewDataActions.setCurrentAction(Action.Edit));
        },
        [dispatch, previewData.data?.data]
    );

    /**
     * Delete click handler
     * @param data Entity data
     */
    const onDeleteClick = useCallback(
        (data: PreviewEntityDTO) => {
            setEditData({
                entityType: data.entityType,
                id: data.id,
                initiator: ApiInitiator.PreviewData
            });
            setDeleteEntityName(data.name);
            dispatch(previewDataActions.setCurrentAction(Action.Delete));
        },
        [dispatch]
    );

    /**
     * Method to handle the action clicked on grid
     * @param action Action Enum eg. Edit and delete
     * @param data row data on which it is clicked
     */
    const handleAction = useCallback(
        (action: number, data: any) => {
            switch (action) {
                case Action.Edit: {
                    onEditClick(data);
                    break;
                }
                case Action.Delete: {
                    onDeleteClick(data);
                    break;
                }
                default:
                    break;
            }
        },
        [onDeleteClick, onEditClick]
    );

    /**
     * Details click handler
     * @param rowData Row data
     */
    const handleDetailsClick = useCallback(
        (rowData: PreviewEntityDTO) => {
            setEditData({
                entityType: rowData.entityType,
                id: rowData.id,
                initiator: ApiInitiator.PreviewData,
                isEditActionDisabled: isDataEditorPreviewDisabled
            });
            dispatch(previewDataActions.setCurrentAction(Action.Details));
        },
        [dispatch]
    );

    /**
     * For handling toggle expansion
     */
    const handleExpansionToggle = useCallback(
        (rowData: PreviewEntityDTO) => {
            dispatch(previewDataActions.toggleExpansion(rowData));
        },
        [dispatch]
    );

    const getColumnDefs = useCallback(
        () => getDataListColDefs(handleExpansionToggle, handleDetailsClick, handleAction),
        [handleAction, handleDetailsClick, handleExpansionToggle]
    );

    const updateColumnDefParams = useCallback(
        (params: any) => {
            params?.api.setColumnDefs([]);
            const newDefs = getColumnDefs().map((def: any) => {
                if (def.field === 'errors') {
                    return {
                        ...def,
                        headerComponentParams: {
                            count: errorCountRef.current,
                            isAtleastOneErrorFixed: errorFixedRef.current
                        }
                    };
                }
                return def;
            });
            params?.api.setColumnDefs(newDefs);
        },
        [getColumnDefs]
    );

    // Calling fetch to get the loaded data
    useEffect(() => {
        dispatch(previewDataActions.loadPreviewData(pagedRequest));
    }, [dispatch, pagedRequest]);

    useEffect(() => {
        errorCountRef.current = errorCount;
        updateColumnDefParams(dataListRef.current);
    }, [errorCount, isAtleastOneErrorFixed, updateColumnDefParams]);

    useEffect(() => {
        errorFixedRef.current = isAtleastOneErrorFixed;
    }, [isAtleastOneErrorFixed]);

    useEffect(() => {
        dispatch(previewDataActions.fetchErrorCount());
    }, [dispatch]);

    /**
     * Delete confirmation handler
     * @param modalData Data to be deleted
     */
    const onDelete = (modalData: EntityDetailsPayload) => {
        dispatch(previewDataActions.deleteData(modalData));
        dispatch(previewDataActions.setCurrentAction(Action.Default));
        const messageModalDetails = {
            isVisible: true,
            header: DELETE_POPUP_SUCCESS,
            message: DELETE_POPUP_MSG,
            type: MessageType.Success
        };
        dispatch(appStateActions.showMessageModal(messageModalDetails));
    };

    const handlePopupClose = () => {
        dispatch(previewDataActions.setCurrentAction(Action.Default));
        dispatch(previewDataActions.setFieldErrors([]));
        setEditData(undefined);
    };

    const handleIndicatorUpdate = (updatedData: IndicatorUpdateDTO) => {
        dispatch(previewDataActions.updateIndicator(updatedData));
    };

    const handleCategoryUpdate = (updatedData: CategoryDTO) => {
        dispatch(previewDataActions.updateCategory(updatedData));
    };

    const handleSubCategoryUpdate = (updatedData: CategoryDTO) => {
        dispatch(previewDataActions.updateSubCategory(updatedData));
    };

    const updateFieldErrors = (errors: Array<FieldError>) => {
        dispatch(previewDataActions.setFieldErrors(errors));
    };

    const handleDataListReady = (params: any) => {
        dataListRef.current = params;
        updateColumnDefParams(params);
    };

    /**
     * To handle infinite scroll
     */
    const handleScroll = () => {
        if (
            previewData.status !== ApiStatus.LOADING &&
            previewData.data?.pagination &&
            previewData.data?.pagination.totalPages > pagedRequest.pageNumber
        ) {
            const updatedPagedRequest: PagedRequest = { ...pagedRequest };
            updatedPagedRequest.pageNumber = updatedPagedRequest.pageNumber + 1;
            setPagedRequest(updatedPagedRequest);
            dispatch(previewDataActions.loadPreviewData(updatedPagedRequest));
        }
    };
    // Renderer
    return (
        <>
            {searchVal && searchVal.length >= 3 ? (
                <PreviewSearchResult
                    searchVal={searchVal}
                    handleDetailsClick={handleDetailsClick}
                    onDeleteClick={onDeleteClick}
                    onEditClick={onEditClick}
                    isDataEditorPreviewDisabled={isDataEditorPreviewDisabled}
                />
            ) : (
                <DataList
                    isNoDataVisible={false}
                    data={previewData.data?.data || []}
                    columnDefs={getColumnDefs()}
                    onReady={handleDataListReady}
                    fetchNextDataSet={handleScroll}
                />
            )}
            <MessageModal
                headerName={DELETE_POPUP_TITLE}
                message={`${DELETE_POPUP_SUMMARY_PART1} ${
                    deleteEntityName.length > 40
                        ? '"' + deleteEntityName.substring(0, 40) + '..."'
                        : '"' + deleteEntityName + '"'
                }. ${DELETE_POPUP_SUMMARY_PART2}`}
                isVisible={currentAction === Action.Delete}
                isSecondaryButtonVisible={true}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                toolTip={`${deleteEntityName}`}
                onPrimaryAction={() => {
                    editData && onDelete(editData);
                }}
                onSecondaryAction={() =>
                    dispatch(previewDataActions.setCurrentAction(Action.Default))
                }
            />
            {editData && currentAction === Action.Details && (
                <EntityDetailsPopup
                    entity={editData}
                    isShowDetailsPopup={currentAction === Action.Details}
                    handleEditClick={onEditClick}
                    closeHandler={() =>
                        dispatch(previewDataActions.setCurrentAction(Action.Default))
                    }
                />
            )}
            {editData?.entityType === EntityType.Indicator && currentAction === Action.Edit && (
                <EditIndicator
                    data={editData}
                    isEditModalVisible={currentAction === Action.Edit}
                    fieldErrors={fieldErrors}
                    onClose={handlePopupClose}
                    onUpdate={handleIndicatorUpdate}
                    updateFieldErrors={updateFieldErrors}
                />
            )}
            {editData?.entityType === EntityType.SubCategory && currentAction === Action.Edit && (
                <SubCategoryEdit
                    data={editData}
                    isEditModalVisible={currentAction === Action.Edit}
                    fieldErrors={fieldErrors}
                    onClose={handlePopupClose}
                    onUpdate={handleSubCategoryUpdate}
                    updateFieldErrors={updateFieldErrors}
                />
            )}
            {editData?.entityType === EntityType.Category && currentAction === Action.Edit && (
                <CategoryEdit
                    data={editData}
                    isEditModalVisible={currentAction === Action.Edit}
                    fieldErrors={fieldErrors}
                    onClose={handlePopupClose}
                    onUpdate={handleCategoryUpdate}
                    updateFieldErrors={updateFieldErrors}
                />
            )}
        </>
    );
};

export default PreviewData;
